import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';

import { ResponsiveContext, ScreenSize } from '../shared/Responsive';

const MAX_CONTENT_WIDTH = 1170;
const COURSE_CARD_WIDTH = 340;
interface Props {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  courseCard: React.ReactNode;
  courseLearningPoints: React.ReactNode;
  newCourses: React.ReactNode;
  signUpButton: React.ReactNode;
}

const SectionContainer = styled.div`
  margin-left: 50px;
  margin-right: 50px;
  max-width: ${MAX_CONTENT_WIDTH}px;
  padding-right: calc(80px + ${COURSE_CARD_WIDTH}px);
  flex-grow: 1;
`;

const CourseTitleContainer = styled.div`
  margin-bottom: 6px;
`;

const CourseSubtitleContainer = styled.div`
  margin-bottom: 26px;
`;

const TopContainer = styled(SectionContainer)`
  padding-bottom: 44px;
  position: relative;
`;

const LearningPointsContainer = styled(SectionContainer)`
  padding-top: 32px;
  padding-bottom: 36px;
`;

const BottomContainer = styled(SectionContainer)`
  padding-top: 58px;
`;

const CourseCardContainer = styled.div`
  background-color: white;
  z-index: 1;
  width: ${COURSE_CARD_WIDTH}px;
  position: absolute;
  top: 0;
  right: 0;
  filter: drop-shadow(0px 3.26421px 16.321px rgba(0, 0, 0, 0.1));
  border-radius: 3px;
`;

const SignUpButtonContainer = styled.div``;

const Section = styled.section`
  display: flex;
  justify-content: center;
`;

const Container = styled.div<{ mobile?: boolean; expanded?: boolean }>`
  position: relative;
  // Includes padding for UpgradeBanner
  padding-top: 46px;
  padding-bottom: 74px;
  box-sizing: content-box;
  color: #222222;
  background-color: #ffffff;

  > ${Section}:nth-child(even) {
    background-color: #f8f8f9;
  }

  &.expanded {
    ${SectionContainer} {
      padding-right: calc(100px + ${COURSE_CARD_WIDTH}px);
    }
  }

  &.mobile {
    padding-top: 30px;

    ${SectionContainer} {
      margin-left: 16px;
      margin-right: 16px;
      padding-right: 0;
    }

    ${TopContainer} {
      width: 100%;
      padding-bottom: 0;

      ${SignUpButtonContainer} {
        margin-bottom: 30px;
      }
    }

    ${LearningPointsContainer} {
      width: 100%;
    }

    ${CourseCardContainer} {
      max-width: none;
      width: 100%;
      position: static;
      filter: none;
      border-radius: 0;
    }
  }
`;

const MarketingPageLayout: React.FunctionComponent<Props> = ({
  title,
  subtitle,
  courseCard,
  courseLearningPoints,
  newCourses,
  signUpButton,
}) => {
  const { screenSize } = useContext(ResponsiveContext);
  const isMobile = screenSize === ScreenSize.Mobile;
  const isLargeScreen = screenSize === ScreenSize.Large;

  const [containerMinHeight, setContainerMinHeight] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const courseCardRef = useRef<HTMLDivElement>(null);

  // The course card is floating on the right, the container should have
  // at least the same height so that the course card does not exceed
  // the container.
  useEffect(() => {
    const handler = () => {
      setTimeout(() => {
        if (courseCardRef.current) {
          setContainerMinHeight(courseCardRef.current.offsetHeight);
        }
      }, 0);
    };
    handler();
    window.addEventListener('resize', handler);

    return () => {
      window.removeEventListener('resize', handler);
    };
  }, []);

  return (
    <>
      <Container
        mobile={isMobile}
        expanded={isLargeScreen}
        ref={containerRef}
        style={{ minHeight: containerMinHeight }}
      >
        <Section>
          <TopContainer>
            <CourseTitleContainer>{title}</CourseTitleContainer>
            <CourseSubtitleContainer>{subtitle}</CourseSubtitleContainer>
            <SignUpButtonContainer>{signUpButton}</SignUpButtonContainer>
            <CourseCardContainer ref={courseCardRef}>
              {courseCard}
            </CourseCardContainer>
          </TopContainer>
        </Section>
        <Section>
          <LearningPointsContainer>
            {courseLearningPoints}
          </LearningPointsContainer>
        </Section>
        <Section>
          <BottomContainer>{newCourses}</BottomContainer>
        </Section>
      </Container>
    </>
  );
};

export default MarketingPageLayout;
